import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  LayoutGrid, FolderOpen, FileText, BarChart3, Building2, Settings, LogOut, User,
  TrendingUp, DollarSign, Activity, Shield, ChevronRight, Banknote, Plus,
  Image as ImageIcon, Table, Wallet, Target, Eye, ArrowRight, AlertTriangle,
  CheckCircle2, Hourglass,
} from 'lucide-react';

/**
 * Visual mockup comparing 3 navigation/IA options for the Command Center launch.
 * Public route /command-center-layout-options - no auth required.
 */

const fmt$ = (n) => '$' + (n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

// --- Reusable mini-sidebar mocks ---
const SidebarMock = ({ items, active }) => (
  <div className="w-[200px] bg-white border-r border-slate-200 rounded-l-2xl overflow-hidden flex flex-col">
    <div className="px-4 py-4 border-b border-slate-100">
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-lg bg-blue-600 text-white flex items-center justify-center font-bold text-xs">D</div>
        <div>
          <p className="font-bold text-slate-900 text-xs leading-tight">DrawReady</p>
          <p className="text-[10px] text-slate-500">Construction Finance</p>
        </div>
      </div>
    </div>
    <div className="flex-1 px-2 py-3 space-y-1">
      {items.map((item, i) => {
        const Icon = item.icon;
        const isActive = item.label === active;
        return (
          <div
            key={i}
            className={`px-3 py-2 rounded-lg flex items-center gap-2 text-xs transition-colors ${
              isActive ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-700 hover:bg-slate-50'
            } ${item.removed ? 'opacity-30 line-through' : ''}`}
          >
            <Icon className="w-3.5 h-3.5 flex-shrink-0" />
            <div className="flex-1 leading-tight">
              <p className="font-medium">{item.label}</p>
              {item.sub && <p className={`text-[9px] ${isActive ? 'text-blue-100' : 'text-slate-400'}`}>{item.sub}</p>}
            </div>
            {item.badge && (
              <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${
                item.badge === 'NEW' ? 'bg-amber-200 text-amber-900' : 'bg-emerald-100 text-emerald-700'
              }`}>
                {item.badge}
              </span>
            )}
          </div>
        );
      })}
    </div>
    <div className="px-2 py-3 border-t border-slate-100">
      <div className="px-3 py-2 rounded-lg flex items-center gap-2 text-xs text-slate-700">
        <User className="w-3.5 h-3.5" />
        <p className="font-medium">Profile</p>
      </div>
      <div className="px-3 py-2 rounded-lg flex items-center gap-2 text-xs text-slate-700">
        <LogOut className="w-3.5 h-3.5" />
        <p className="font-medium">Logout</p>
      </div>
    </div>
  </div>
);

// --- KPI strip used at top of every Command Center mockup ---
const KpiStrip = () => (
  <div className="grid grid-cols-4 gap-3 mb-4">
    <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-3 border border-emerald-200">
      <div className="flex items-center justify-between mb-1">
        <Wallet className="w-4 h-4 text-emerald-700" />
        <span className="text-[9px] font-medium text-emerald-700 bg-emerald-200 px-1.5 py-0.5 rounded-full">Available</span>
      </div>
      <p className="text-base font-bold text-slate-900 font-mono">$730,000</p>
      <p className="text-[10px] text-slate-500">Across 5 active loans</p>
    </div>
    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-3 border border-blue-200">
      <div className="flex items-center justify-between mb-1">
        <Activity className="w-4 h-4 text-blue-700" />
        <span className="text-[9px] font-medium text-blue-700 bg-blue-200 px-1.5 py-0.5 rounded-full">Daily Burn</span>
      </div>
      <p className="text-base font-bold text-slate-900 font-mono">$4,419</p>
      <p className="text-[10px] text-slate-500">Month-to-date</p>
    </div>
    <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-3 border border-purple-200">
      <div className="flex items-center justify-between mb-1">
        <Target className="w-4 h-4 text-purple-700" />
        <span className="text-[9px] font-medium text-purple-700 bg-purple-200 px-1.5 py-0.5 rounded-full">Bank-Ready</span>
      </div>
      <p className="text-base font-bold text-slate-900 font-mono">82<span className="text-xs text-slate-500">/100</span></p>
      <p className="text-[10px] text-slate-500">Portfolio score</p>
    </div>
    <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-3 border border-amber-200">
      <div className="flex items-center justify-between mb-1">
        <Shield className="w-4 h-4 text-amber-700" />
        <span className="text-[9px] font-medium text-amber-700 bg-amber-200 px-1.5 py-0.5 rounded-full">Compliance</span>
      </div>
      <p className="text-base font-bold text-slate-900 font-mono">7 <span className="text-xs text-slate-500">/ 12</span></p>
      <p className="text-[10px] text-slate-500">Contractors approved</p>
    </div>
  </div>
);

const LoansTable = ({ rows = 5 }) => (
  <div className="bg-white border border-slate-200 rounded-xl overflow-hidden mb-4">
    <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
      <div>
        <h3 className="font-bold text-slate-900 text-sm">Active Loans</h3>
        <p className="text-[10px] text-slate-500">Click any row to open project</p>
      </div>
      <Button variant="ghost" size="sm" className="text-xs h-7">View All <ArrowRight className="w-3 h-3 ml-1" /></Button>
    </div>
    <div className="divide-y divide-slate-100">
      {[
        { proj: 'New Build 38th', addr: 'Cedar Rapids, IA', loan: 2600000, drawn: 1870000, burn: 147, score: 82, term: 12, agent: 'Pearson Realty' },
        { proj: 'Solon Modern Farmhouse', addr: 'Solon, IA', loan: 1400000, drawn: 312000, burn: 78, score: 91, term: 9, agent: 'Owner-listed' },
        { proj: 'Iowa City Renovation', addr: 'Iowa City, IA', loan: 480000, drawn: 410000, burn: 24, score: 95, term: 6, agent: 'Rental' },
        { proj: 'Marion Spec Home', addr: 'Marion, IA', loan: 720000, drawn: 110000, burn: 41, score: 65, term: 12, agent: 'Skogman Realty' },
        { proj: '388 Ash Ave', addr: 'Iowa City, IA', loan: 22565, drawn: 0, burn: 0, score: 70, term: 24, agent: 'N/A' },
      ].slice(0, rows).map((p, i) => (
        <div key={i} className="px-4 py-2.5 hover:bg-slate-50 cursor-pointer flex items-center gap-2 text-xs">
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-slate-900 truncate">{p.proj}</p>
            <div className="flex items-center gap-1.5 text-[9px] text-slate-500 truncate">
              <span>{p.addr}</span>
              <span>&middot;</span>
              <span className="text-slate-600">{p.agent}</span>
            </div>
          </div>
          <div className="text-right w-20">
            <p className="font-mono text-slate-700">{fmt$(p.loan)}</p>
            <p className="text-[9px] text-slate-400">Loan</p>
          </div>
          <div className="text-center w-12">
            <p className="font-mono text-slate-700">{p.term}<span className="text-[9px] text-slate-400">mo</span></p>
            <p className="text-[9px] text-slate-400">Term</p>
          </div>
          <div className="text-right w-14">
            <p className="font-mono text-blue-600">${p.burn}/d</p>
            <p className="text-[9px] text-slate-400">Burn</p>
          </div>
          <div className="text-right w-10">
            <p className={`font-bold ${p.score >= 85 ? 'text-emerald-600' : p.score >= 70 ? 'text-blue-600' : 'text-amber-600'}`}>
              {p.score}
            </p>
            <p className="text-[9px] text-slate-400">Score</p>
          </div>
          <ChevronRight className="w-3 h-3 text-slate-300" />
        </div>
      ))}
    </div>
  </div>
);

const UnderwriteCard = () => (
  <div className="bg-gradient-to-br from-indigo-600 to-purple-700 rounded-xl p-4 text-white mb-4">
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
        <BarChart3 className="w-5 h-5" />
      </div>
      <div className="flex-1">
        <p className="text-xs font-bold uppercase tracking-wide text-indigo-200">Underwrite a Deal</p>
        <h3 className="font-bold text-base mb-1">Financial Analysis Tools</h3>
        <p className="text-[11px] text-indigo-100 mb-2">
          Run cash-flow projections, ROI, IRR, and stress tests on potential deals before funding.
        </p>
        <Button size="sm" variant="secondary" className="bg-white text-indigo-700 hover:bg-indigo-50 text-xs h-7">
          Open Analysis Suite <ArrowRight className="w-3 h-3 ml-1" />
        </Button>
      </div>
    </div>
  </div>
);

const ComplianceCard = () => {
  const contractors = [
    { name: 'Lowden Plumbing', trade: 'HVAC', w9: 'approved', coi: 'approved', lien: 'pending' },
    { name: 'Apex Framing LLC', trade: 'Framing', w9: 'approved', coi: 'expiring', lien: 'approved' },
    { name: 'BlueSky Roofing', trade: 'Roofing', w9: 'awaiting', coi: 'awaiting', lien: 'awaiting' },
    { name: 'Iowa Concrete Co.', trade: 'Foundation', w9: 'approved', coi: 'approved', lien: 'approved' },
    { name: 'Cedar Electric', trade: 'Electrical', w9: 'awaiting', coi: 'awaiting', lien: 'awaiting' },
    { name: 'Prime Drywall', trade: 'Drywall', w9: 'approved', coi: 'approved', lien: 'submitted' },
    { name: 'Summit Painting', trade: 'Paint', w9: 'submitted', coi: 'submitted', lien: 'awaiting' },
    { name: 'Mid-IA HVAC', trade: 'HVAC', w9: 'approved', coi: 'approved', lien: 'approved' },
    { name: 'Tile Masters', trade: 'Tile', w9: 'awaiting', coi: 'awaiting', lien: 'awaiting' },
  ];
  const pillCls = (s) => ({
    approved: 'bg-emerald-100 text-emerald-700',
    submitted: 'bg-blue-100 text-blue-700',
    awaiting: 'bg-red-100 text-red-700',
    expiring: 'bg-orange-100 text-orange-700',
    pending: 'bg-amber-100 text-amber-700',
  }[s] || 'bg-slate-100 text-slate-600');
  const pillLabel = (s) => ({
    approved: '\u2713', submitted: 'Sub', awaiting: '!', expiring: 'Exp', pending: 'Pend',
  }[s] || s);
  return (
    <div className="bg-white border border-slate-200 rounded-xl mb-4 flex flex-col" style={{ maxHeight: 400 }}>
      <div className="px-3 py-2.5 border-b border-slate-100 flex items-center justify-between flex-shrink-0">
        <div>
          <h3 className="font-bold text-slate-900 text-sm">Contractor Compliance</h3>
          <p className="text-[10px] text-slate-500">W9 &middot; COI &middot; Lien Waiver</p>
        </div>
        <Badge variant="secondary" className="text-[10px]">{contractors.length}</Badge>
      </div>
      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-1.5">
        {contractors.map((c, i) => (
          <div key={i} className="p-2 bg-slate-50 hover:bg-slate-100 rounded-md cursor-pointer">
            <div className="flex items-center justify-between mb-1">
              <span className="font-semibold text-slate-900 text-[11px] truncate flex-1">{c.name}</span>
              <span className="text-[9px] text-slate-500 ml-1">{c.trade}</span>
            </div>
            <div className="flex items-center gap-1">
              <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${pillCls(c.w9)}`}>W9 {pillLabel(c.w9)}</span>
              <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${pillCls(c.coi)}`}>COI {pillLabel(c.coi)}</span>
              <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${pillCls(c.lien)}`}>Lien {pillLabel(c.lien)}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="px-3 py-2 border-t border-slate-100 flex-shrink-0">
        <Button variant="ghost" size="sm" className="w-full text-[10px] h-7">View All Compliance <ArrowRight className="w-3 h-3 ml-1" /></Button>
      </div>
    </div>
  );
};

const MiniProjectCard = ({ name, addr, status, budget, progress }) => (
  <div className="bg-white rounded-lg border border-slate-200 overflow-hidden hover:shadow-md transition-shadow cursor-pointer">
    <div className="h-14 bg-gradient-to-br from-slate-200 to-slate-300 relative">
      <span className={`absolute top-1.5 right-1.5 text-[9px] px-1.5 py-0.5 rounded-full font-medium ${
        status === 'Active' ? 'bg-emerald-500 text-white' : 'bg-white text-slate-700'
      }`}>{status}</span>
    </div>
    <div className="p-2">
      <p className="font-semibold text-slate-900 text-[11px] truncate">{name}</p>
      <p className="text-[9px] text-slate-500 truncate">{addr}</p>
      <div className="flex items-center justify-between mt-1.5">
        <span className="text-[10px] font-mono text-slate-700">{fmt$(budget)}</span>
        <span className="text-[10px] text-blue-600 font-medium">{progress}%</span>
      </div>
    </div>
  </div>
);

const ProjectGridFull = () => (
  <div className="grid grid-cols-3 gap-3">
    {[
      { name: '38', addr: '1615 38th St', status: 'Scheduled', budget: 410000, progress: 100 },
      { name: 'New Build 38th', addr: '1615 38th St SE', status: 'Active', budget: 409760, progress: 44 },
      { name: 'Ellis Row', addr: '1319-1333 Ellis Row', status: 'Active', budget: 1440000, progress: 100 },
      { name: '388 Ash Ave', addr: '123 Main St', status: 'Active', budget: 22565, progress: 100 },
      { name: 'Test Home Build', addr: '123 Test Street', status: 'Active', budget: 350000, progress: 100 },
      { name: 'TEST_SendToMore', addr: '123 Test Street', status: 'Active', budget: 0, progress: 0 },
    ].map((p, i) => <MiniProjectCard key={i} {...p} />)}
  </div>
);

// === Layout A: Command Center ONLY ===
const LayoutA = () => (
  <div className="flex bg-slate-50 rounded-2xl border border-slate-200 overflow-hidden shadow-lg" style={{ minHeight: 720 }}>
    <SidebarMock
      active="Command Center"
      items={[
        { label: 'Command Center', sub: 'Overview & insights', icon: LayoutGrid, badge: 'NEW' },
        { label: 'Project Hub', sub: 'All projects', icon: FolderOpen },
        { label: 'Reports', sub: 'Exports & history', icon: FileText },
        { label: 'Financial Analysis', icon: BarChart3, removed: true },
      ]}
    />
    <div className="flex-1 p-5 overflow-y-auto">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Command Center</h1>
          <p className="text-xs text-slate-500">Welcome back, Mod — here&apos;s your portfolio at a glance</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="h-8 text-xs"><FolderOpen className="w-3 h-3 mr-1" />View All Projects</Button>
          <Button size="sm" className="bg-blue-600 hover:bg-blue-700 h-8 text-xs"><Plus className="w-3 h-3 mr-1" />New Project</Button>
        </div>
      </div>
      <KpiStrip />
      <div className="grid grid-cols-3 gap-4">
        <div className="col-span-2">
          <LoansTable rows={5} />
          <UnderwriteCard />
        </div>
        <div>
          <ComplianceCard />
          <div className="bg-white border border-slate-200 rounded-xl p-3 text-xs">
            <h4 className="font-bold text-slate-900 mb-2">Quick Actions</h4>
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 p-1.5 bg-slate-50 rounded hover:bg-slate-100 cursor-pointer">
                <DollarSign className="w-3 h-3 text-emerald-600" /><span>Request Draw</span>
              </div>
              <div className="flex items-center gap-2 p-1.5 bg-slate-50 rounded hover:bg-slate-100 cursor-pointer">
                <FileText className="w-3 h-3 text-blue-600" /><span>Generate Report</span>
              </div>
              <div className="flex items-center gap-2 p-1.5 bg-slate-50 rounded hover:bg-slate-100 cursor-pointer">
                <BarChart3 className="w-3 h-3 text-purple-600" /><span>Underwrite Deal</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// === Layout B: Command Center + Grid View toggle ===
const LayoutB = () => {
  const [view, setView] = useState('command');
  return (
    <div className="flex bg-slate-50 rounded-2xl border border-slate-200 overflow-hidden shadow-lg" style={{ minHeight: 720 }}>
      <SidebarMock
        active="Dashboard"
        items={[
          { label: 'Dashboard', sub: 'Command Center', icon: LayoutGrid, badge: 'NEW' },
          { label: 'Project Hub', sub: 'Bid sheets, banks', icon: FolderOpen },
          { label: 'Reports', sub: 'Exports & history', icon: FileText },
          { label: 'Financial Analysis', icon: BarChart3, removed: true },
        ]}
      />
      <div className="flex-1 p-5 overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h1 className="text-xl font-bold text-slate-900">Dashboard</h1>
            <p className="text-xs text-slate-500">Switch between Command Center insights and project views</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="bg-white border border-slate-200 rounded-lg p-0.5 flex">
              <button
                onClick={() => setView('command')}
                className={`px-2.5 py-1 text-[10px] rounded-md font-medium transition-colors ${
                  view === 'command' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                <Activity className="w-3 h-3 inline mr-1" />Command Center
              </button>
              <button
                onClick={() => setView('grid')}
                className={`px-2.5 py-1 text-[10px] rounded-md font-medium transition-colors ${
                  view === 'grid' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                <ImageIcon className="w-3 h-3 inline mr-1" />Visual Grid
              </button>
              <button
                onClick={() => setView('ledger')}
                className={`px-2.5 py-1 text-[10px] rounded-md font-medium transition-colors ${
                  view === 'ledger' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                <Table className="w-3 h-3 inline mr-1" />Ledger
              </button>
            </div>
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700 h-8 text-xs"><Plus className="w-3 h-3 mr-1" />New</Button>
          </div>
        </div>
        {view === 'command' && (
          <>
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 mb-3 flex items-center gap-2 text-[11px] text-blue-900">
              <Eye className="w-3.5 h-3.5 text-blue-600" />
              <span>Want to browse projects visually? Use the toggle above or</span>
              <button onClick={() => setView('grid')} className="font-bold underline hover:no-underline">switch to Visual Grid</button>
            </div>
            <KpiStrip />
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2">
                <LoansTable rows={5} />
                <UnderwriteCard />
              </div>
              <div>
                <ComplianceCard />
              </div>
            </div>
          </>
        )}
        {view === 'grid' && (
          <>
            <div className="bg-white rounded-xl p-3 border border-slate-200 mb-3 flex items-center justify-between">
              <div className="text-xs">
                <span className="font-bold text-slate-900">$230,741</span> <span className="text-slate-500">total available</span>
              </div>
              <div className="text-[10px] text-slate-500">5 Active &middot; 1 Scheduled &middot; $2.6M total budget</div>
            </div>
            <ProjectGridFull />
          </>
        )}
        {view === 'ledger' && (
          <div className="bg-white rounded-xl border border-slate-200 p-4 text-center text-xs text-slate-500">
            <Table className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            (Existing Financial Ledger table view — preserved as-is)
          </div>
        )}
      </div>
    </div>
  );
};

// === Layout C: Command Center with embedded compact carousel ===
const LayoutC = () => (
  <div className="flex bg-slate-50 rounded-2xl border border-slate-200 overflow-hidden shadow-lg" style={{ minHeight: 720 }}>
    <SidebarMock
      active="Command Center"
      items={[
        { label: 'Command Center', sub: 'Overview & insights', icon: LayoutGrid, badge: 'NEW' },
        { label: 'Project Hub', sub: 'All projects', icon: FolderOpen },
        { label: 'Reports', sub: 'Exports & history', icon: FileText },
        { label: 'Financial Analysis', icon: BarChart3, removed: true },
      ]}
    />
    <div className="flex-1 p-5 overflow-y-auto">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Command Center</h1>
          <p className="text-xs text-slate-500">Insights + your projects, all in one place</p>
        </div>
        <Button size="sm" className="bg-blue-600 hover:bg-blue-700 h-8 text-xs"><Plus className="w-3 h-3 mr-1" />New Project</Button>
      </div>
      <KpiStrip />

      {/* Embedded compact project strip */}
      <div className="bg-white border border-slate-200 rounded-xl p-3 mb-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-bold text-slate-900 text-sm">Your Projects</h3>
          <Button variant="ghost" size="sm" className="text-xs h-7">View All in Project Hub <ArrowRight className="w-3 h-3 ml-1" /></Button>
        </div>
        <div className="grid grid-cols-4 gap-2">
          {[
            { name: '38', addr: '1615 38th St', status: 'Scheduled', budget: 410000, progress: 100 },
            { name: 'New Build 38th', addr: '1615 38th SE', status: 'Active', budget: 409760, progress: 44 },
            { name: 'Ellis Row', addr: '1319-1333 Ellis', status: 'Active', budget: 1440000, progress: 100 },
            { name: '388 Ash Ave', addr: '123 Main St', status: 'Active', budget: 22565, progress: 100 },
          ].map((p, i) => <MiniProjectCard key={i} {...p} />)}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="col-span-2">
          <LoansTable rows={3} />
          <UnderwriteCard />
        </div>
        <div>
          <ComplianceCard />
        </div>
      </div>
    </div>
  </div>
);

// === MAIN PAGE ===
export default function CommandCenterLayoutOptions() {
  const [active, setActive] = useState('B');

  const options = [
    {
      id: 'A',
      title: 'Option A — Command Center ONLY',
      tagline: 'Clean, focused dashboard. No grid view.',
      pros: ['Cleanest, most opinionated experience', 'Forces all "browse projects" behavior into Project Hub', 'Best for power users who think in metrics'],
      cons: ['Loses the visual project tile experience on the landing page', 'No quick at-a-glance project thumbnails'],
    },
    {
      id: 'B',
      title: 'Option B — Command Center + View Toggle',
      tagline: 'Toggle between Command Center, Visual Grid, and Ledger views.',
      pros: ['Preserves all 3 existing layouts as toggle options', 'Power users get insights, visual users keep their grid', 'Familiar to existing users — no behavior loss'],
      cons: ['Slightly more crowded UI (extra toggle)', 'Two paths to projects (toggle + Project Hub)'],
      recommended: true,
    },
    {
      id: 'C',
      title: 'Option C — Command Center with Embedded Project Strip',
      tagline: 'Insights at top, compact project carousel inline, then loans + compliance.',
      pros: ['Single scrolling page with everything', 'Visual projects still front-and-center', 'No mode switching'],
      cons: ['Page becomes long', 'Compact tiles lose photo impact'],
    },
  ];

  const activeOpt = options.find((o) => o.id === active);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-slate-100 px-6 py-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <Badge className="mb-2 bg-purple-100 text-purple-700 hover:bg-purple-100">DESIGN PREVIEW</Badge>
          <h1 className="text-3xl font-bold text-slate-900 mb-1">Command Center — Navigation & Layout Options</h1>
          <p className="text-sm text-slate-600 max-w-3xl">
            Three ways to integrate the Financial Command Center into the live app. All three retire the standalone
            "Financial Analysis" link (absorbed into Command Center as the "Underwrite a Deal" module). Pick A, B, or C.
          </p>
        </div>

        {/* Option selector tabs */}
        <div className="bg-white rounded-2xl border border-slate-200 p-2 mb-6 inline-flex gap-1 shadow-sm">
          {options.map((opt) => (
            <button
              key={opt.id}
              onClick={() => setActive(opt.id)}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                active === opt.id
                  ? 'bg-slate-900 text-white shadow-md'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
              data-testid={`option-tab-${opt.id}`}
            >
              Option {opt.id}
              {opt.recommended && (
                <span className="ml-1.5 text-[9px] bg-emerald-200 text-emerald-800 px-1.5 py-0.5 rounded-full">REC</span>
              )}
            </button>
          ))}
        </div>

        {/* Option header */}
        <Card className="p-5 mb-5 bg-white border-2 border-slate-200">
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div className="flex-1 min-w-[280px]">
              <div className="flex items-center gap-2 mb-1">
                <h2 className="text-xl font-bold text-slate-900">{activeOpt.title}</h2>
                {activeOpt.recommended && (
                  <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Recommended</Badge>
                )}
              </div>
              <p className="text-sm text-slate-600">{activeOpt.tagline}</p>
            </div>
            <div className="flex gap-4 text-xs">
              <div>
                <p className="font-bold text-emerald-700 mb-1 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />Pros</p>
                <ul className="space-y-0.5 text-slate-700">
                  {activeOpt.pros.map((p, i) => <li key={i}>&middot; {p}</li>)}
                </ul>
              </div>
              <div>
                <p className="font-bold text-amber-700 mb-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Cons</p>
                <ul className="space-y-0.5 text-slate-700">
                  {activeOpt.cons.map((p, i) => <li key={i}>&middot; {p}</li>)}
                </ul>
              </div>
            </div>
          </div>
        </Card>

        {/* Visual mockup */}
        {active === 'A' && <LayoutA />}
        {active === 'B' && <LayoutB />}
        {active === 'C' && <LayoutC />}

        {/* Notes */}
        <div className="mt-6 grid grid-cols-3 gap-4 text-xs">
          <Card className="p-4 bg-white">
            <p className="font-bold text-slate-900 mb-1 flex items-center gap-1"><Banknote className="w-3.5 h-3.5 text-emerald-600" />Reports tab</p>
            <p className="text-slate-600">Stays standalone in all 3 options. Recent reports widget could be added to the Command Center sidebar later.</p>
          </Card>
          <Card className="p-4 bg-white">
            <p className="font-bold text-slate-900 mb-1 flex items-center gap-1"><BarChart3 className="w-3.5 h-3.5 text-purple-600" />Financial Analysis</p>
            <p className="text-slate-600">Removed from sidebar in all 3. Re-surfaced as the "Underwrite a Deal" card on Command Center.</p>
          </Card>
          <Card className="p-4 bg-white">
            <p className="font-bold text-slate-900 mb-1 flex items-center gap-1"><FolderOpen className="w-3.5 h-3.5 text-blue-600" />Project Hub</p>
            <p className="text-slate-600">Always available in the sidebar. Becomes the canonical "browse all projects" destination.</p>
          </Card>
        </div>
      </div>
    </div>
  );
}
